import {
  ICPSettingsOverride,
  IToggleButton,
  IToggleButtonToReturnBoolean,
} from "../../helper/tradeSettingsHelper/tradeSettingsInterfaces";
import {
  completeActionColour,
  mutedTimeToActionColour,
  timeToActionColour,
} from "../../helper/statusCellStylingHelper";

// Whether a CP currently shows an override border on any cell — green (a saved
// override that matches the server baseline) or orange (a pending change vs the
// baseline). Crucially this includes a REVERTED override: once the user clears a
// CP back to defaults its current override is empty, but the baseline still
// holds the old server value, so the cell stays orange ("needs Update"). The
// border therefore depends on BOTH the current override and the baseline — a CP
// shows a border whenever either is non-empty. The "All" lock and the Custom
// grid key off this so they can never disagree with the colouring the user
// sees. LQC-1119.
export function cpHasOverrideOrPendingChange(
  override: ICPSettingsOverride | undefined,
  baseline: ICPSettingsOverride | undefined,
): boolean {
  return (
    (override !== undefined && Object.keys(override).length > 0) ||
    (baseline !== undefined && Object.keys(baseline).length > 0)
  );
}

// Compare a cpSettingsOverrides field value against its baseline equivalent.
// Toggle-array fields are compared by the "active" option's name (booleanValue
// maps 1:1 to name), not by reference. Scalar fields use strict equality, which
// correctly treats undefined-vs-present as different.
export function isSameOverrideFieldValue(a: any, b: any): boolean {
  const aIsArr = Array.isArray(a);
  const bIsArr = Array.isArray(b);
  if (aIsArr || bIsArr) {
    const aName = aIsArr ? a.find((o: any) => o.active)?.name : undefined;
    const bName = bIsArr ? b.find((o: any) => o.active)?.name : undefined;
    return aName === bName;
  }
  return a === b;
}

// Extract the current active primitive from a trade-wide setting toggle array.
// Boolean toggles (IToggleButtonToReturnBoolean) return booleanValue; string
// toggles (IToggleButton) return the active option's name.
function getActiveTradeWidePrimitive(
  settingArray: IToggleButton[] | IToggleButtonToReturnBoolean[],
): string | boolean | undefined {
  const active = settingArray.find((o) => o.active);
  if (!active) return undefined;
  return "booleanValue" in active
    ? (active as IToggleButtonToReturnBoolean).booleanValue
    : (active as IToggleButton).name;
}

// Returns true when the current toggle-array value diverges from the stored
// primitive baseline (boolean for autoAffirm — the only remaining trade-wide
// field). Returns false when baseline is absent — no baseline, no pending state.
export function isTradeWideFieldPending(
  settingArray: IToggleButton[] | IToggleButtonToReturnBoolean[],
  baselineValue: string | boolean | undefined,
): boolean {
  if (baselineValue === undefined) return false;
  return getActiveTradeWidePrimitive(settingArray) !== baselineValue;
}

// Returns true when a trade-level CP-overridable field diverges from its
// baseline snapshot (editTradeLevelBaselineByEsid). This is the "pending trade-
// level default" signal — it drives the full orange on the trade-level row and
// the muted orange on CPs that follow the default. Returns false when there's no
// baseline (New trade) or no snapshot for the field.
export function isTradeLevelFieldPending(
  settings: Record<string, any>,
  tradeLevelBaseline: Partial<ICPSettingsOverride> | undefined,
  field: keyof ICPSettingsOverride,
): boolean {
  if (!tradeLevelBaseline) return false;
  const baselineValue = tradeLevelBaseline[field];
  if (baselineValue === undefined) return false;
  return !isSameOverrideFieldValue(settings[field], baselineValue);
}

// LQC-2170: Day and Time expiry options are individually flag-gated so GTC can
// ship first. A gated-off option renders disabled in the dropdown, not hidden.
export function isExpiryTypeOptionDisabled(
  optionName: string | undefined,
  flags: { expiryTypeDay: boolean; expiryTypeTime: boolean },
): boolean {
  if (optionName === "Day") return !flags.expiryTypeDay;
  if (optionName === "Time") return !flags.expiryTypeTime;
  return false;
}

// Border colour semantics for settings cells (precedence top → bottom):
//   - isPending          → orange (direct unsent change vs the baseline)
//   - isOverridden        → green (saved override, differs from trade default)
//   - isFollowingPending  → muted orange (no override; following a pending
//                           trade-level default — updating, but still linked)
//   - none                → undefined (caller applies the default gray)
// For New trades no baseline is set, so isPending collapses to isOverridden
// at the call site and only orange appears — preserving the original behaviour.
export function resolveOverrideBorder(
  isOverridden?: boolean,
  isPending?: boolean,
  isFollowingPending?: boolean,
): string | undefined {
  if (isPending) return timeToActionColour;
  if (isOverridden) return completeActionColour;
  if (isFollowingPending) return mutedTimeToActionColour;
  return undefined;
}
